import { createFileRoute, Link } from "@tanstack/react-router";
import { canonicalLink, canonicalUrl, pathFromMatch, routeMeta } from "@/lib/seo";
import { pergolaHeadScripts } from "@/lib/pergolaJsonLd";
import { ServicePage } from "@/components/ServicePage";
import { PergolaCityLinks } from "@/components/PergolaCityLinks";
import hero from "@/assets/louvered-pergola-hero.jpg";
import pergola4 from "@/assets/pergolux-pergola-4.jpg";
import pergola4Pro from "@/assets/pergolux-pergola-4-pro.jpg";
import pergola4ProMax from "@/assets/pergolux-pergola-4-pro-max.jpg";

const PATH = "/louvered-pergolas";
const TITLE = "PERGOLUX Pergolas in Texas | Texas Made Shade";
const DESC =
  "Shop PERGOLUX aluminum pergolas and accessories with Texas Made Shade. Manual and motorized louvered roofs for San Antonio, Austin and Central Texas.";

const PERGOLUX_MODELS = [
  {
    name: "PERGOLUX Pergola 4",
    type: "Manual louvered roof",
    image: pergola4,
    alt: "PERGOLUX Pergola 4 manual aluminum louvered pergola over an outdoor dining area",
    description:
      "A fourth-generation aluminum pergola with a manually adjustable roof. It delivers adjustable shade without motors or wiring and includes the SnapFIT channel for compatible add-ons.",
    features: ["Manual adjustable louvers", "Four colors and seven standard sizes", "Optional screens and glass walls"],
  },
  {
    name: "PERGOLUX Pergola 4 Pro",
    type: "Motorized louvered roof",
    image: pergola4Pro,
    alt: "PERGOLUX Pergola 4 Pro motorized aluminum pergola with integrated lighting",
    description:
      "A motorized aluminum louvered pergola with integrated beam lighting. Operate the roof by remote, app or compatible smart-home connection.",
    features: ["Remote, app or smart-home control", "Integrated dimmable LED lighting", "Optional screens and glass walls"],
  },
  {
    name: "PERGOLUX Pergola 4 Pro Max",
    type: "Premium motorized louvered roof",
    image: pergola4ProMax,
    alt: "PERGOLUX Pergola 4 Pro Max premium aluminum pergola on a residential patio",
    description:
      "The premium Series 4 system pairs a thicker structural profile with a motorized roof and lighting integrated into the louvers for a finished outdoor-room look.",
    features: ["Premium structural profile", "Louver-integrated LED lighting", "Optional screens and glass walls"],
  },
];

/** Investment ladder: fabric shade → louvered outdoor room → custom structure */
const LADDER = [
  {
    step: "Step 1",
    name: "Retractable Awning",
    invest: "Lower investment",
    blurb:
      "Motorized fabric shade that retracts out of the way. Perfect when you want shade on demand and a smaller budget.",
    to: "/retractable-awnings",
    cta: "Retractable Awnings",
  },
  {
    step: "Step 2",
    name: "Motorized Louvered Pergola",
    invest: "Premium permanent outdoor room",
    blurb:
      "Extruded aluminum structure with rotating roof louvers. Open for breeze and sun, closed for a rain-resistant roof with hidden gutters and downspouts through the posts.",
    to: "/louvered-pergolas",
    cta: "You're here",
    featured: true,
  },
  {
    step: "Step 3",
    name: "Custom Tension / Architectural Structure",
    invest: "Large custom & commercial",
    blurb:
      "Engineered sail and tension structures for big spans, parking, schools and commercial properties — designed and fabricated in our Seguin shop.",
    to: "/sail-shades",
    cta: "Shade & Tension Structures",
  },
];

const CONFIGS = [
  {
    name: "Attached Pergola",
    blurb:
      "Ledger-mounted to the house or building on one side, posts on the other. The most common backyard configuration — it reads as an extension of the home.",
    specs: ["House-attached beam", "2+ posts", "Best for back patios & outdoor kitchens"],
  },
  {
    name: "Freestanding Pergola",
    blurb:
      "Four or more posts, no building attachment. The right answer when siding, stucco, roof framing or HOA rules make attaching a bad idea — or when the space sits away from the house.",
    specs: ["4+ posts", "No attachment needed", "Pool decks, fire pits, standalone lounges"],
  },
  {
    name: "Multi-Bay System",
    blurb:
      "Multiple pergola modules coupled together to cover big patios, restaurant seating and commercial courtyards under one continuous roof line.",
    specs: ["Ganged modules", "Shared posts & drainage", "Restaurants, hotels, resorts, event venues"],
  },
];

const UPGRADES = [
  { name: "Motorized Screens", blurb: "Zip/side-track drop screens between the posts for sun, wind, bug and privacy control.", to: "/exterior-vertical-shades" },
  { name: "Bosso Smart Lighting", blurb: "Integrated LED lighting in the louvers and perimeter beams, dimmable and scene-controlled.", to: "/outdoor-lighting" },
  { name: "Outdoor Curtains", blurb: "Sunbrella® drapery panels for softness, shade and privacy between posts.", to: "/curtains" },
  { name: "Fans & Heaters", blurb: "Beam-mounted fans and infrared heaters extend the season on both ends.", to: "/hill-country-series" },
  { name: "Misting & Cooling", blurb: "Aeromist fan misting and high-pressure lines routed inside the frame for August afternoons.", to: "/outdoor-cooling-misting" },
  { name: "Outdoor Heating & Cooling", blurb: "Infrared patio heat and cooling equipment so the pergola works in January too.", to: "/outdoor-hvac" },
  { name: "Smart Automation", blurb: "One app for louvers, screens, lighting, fans and heat — plus rain and wind sensors.", to: "/lone-star-elite" },
  { name: "Privacy Walls & Panels", blurb: "Louvered or fabric privacy walls that block the neighbor's view without boxing you in.", to: "/gazebo-privacy-panels" },
  { name: "Service & Maintenance", blurb: "Annual inspection of moving parts, louver cleaning and gutter clean-out under Ranger Care™.", to: "/ranger-care" },
];

/** Q&A rendered on the page — merged into the single FAQPage node in head(). */
const PAGE_FAQS = [
      {
        q: "Will a closed louvered roof really keep rain off?",
        a: "Yes. Closed louvers overlap like shingles, and the perimeter beams carry integrated gutters that drain through the posts — so water leaves the structure instead of curtaining off the edge.",
      },
      {
        q: "What does a louvered pergola cost?",
        a: "Planning numbers: 12' x 16' around $35,000–$48,000 installed, 16' x 20' around $48,000–$65,000+, and complete outdoor-living packages with screens, lighting, fans and misting commonly $60,000–$120,000+. Foundations, electrical, engineering and permits affect every project, so we quote in writing and itemize.",
      },
      {
        q: "Can I finance it?",
        a: "Yes — all of these systems qualify for financing through Acorn Finance. Checking your options takes about a minute and doesn't affect your credit score. Use the Financing Available button on any page.",
      },
      {
        q: "Attached or freestanding?",
        a: "Attached is cleanest when the wall framing, roof line and fascia can carry the ledger. Freestanding is the safer call for stucco, questionable framing, HOA restrictions, or a lounge area away from the house. We tell you which one your site actually wants after we measure.",
      },
      {
        q: "How is drainage handled?",
        a: "Gutters are integrated into the perimeter beams and downspouts run inside the posts. We plan where the water lands — away from foundations, walkways and outdoor kitchens — before your slab work starts.",
      },
      {
        q: "Do I need a permit?",
        a: "Usually yes for a permanent structure, and often HOA approval too. We prepare drawings, provide wind-load and foundation documentation, and coordinate electrical so your permit and HOA package is complete the first time.",
      },
      {
        q: "How long does installation take?",
        a: "Most residential systems install in two to five days on site once the structure arrives and the footings have cured. Fabrication lead time is typically several weeks; multi-bay and commercial projects are scheduled around engineering and permitting.",
      },
      {
        q: "Which manufacturer do you represent?",
        a: "Texas Made Shade now sells PERGOLUX aluminum pergolas and compatible pergola accessories. We help customers compare manual and motorized Series 4 models, choose the right size and plan compatible screens, glass walls and lighting.",
      },
];

export const Route = createFileRoute("/louvered-pergolas")({
  head: (ctx) => ({
    links: [canonicalLink(pathFromMatch(ctx))],
    meta: routeMeta(ctx, {
      title: TITLE,
      description: DESC,
      image: hero,
      imageAlt:
        "Motorized aluminum louvered pergola over a Texas Hill Country patio at sunset with outdoor kitchen and lounge seating",
      socialTitle: "PERGOLUX Pergolas — Texas Outdoor Rooms",
      socialDescription:
        "Aluminum louvered roof pergolas for San Antonio, Austin & the Hill Country. Attached, freestanding & multi-bay outdoor rooms with screens, lighting, fans, misting and automation. Financing available.",
      keywords:
        "PERGOLUX pergola Texas, PERGOLUX dealer Texas, PERGOLUX Pergola 4, PERGOLUX Pergola 4 Pro, PERGOLUX Pergola 4 Pro Max, PERGOLUX accessories, motorized louvered pergola, louvered pergola San Antonio, louvered pergola Austin TX, aluminum pergola Texas, opening roof pergola, motorized louvered patio cover, adjustable louver pergola, outdoor room pergola, freestanding aluminum pergola, attached louvered pergola, pergola screens, pergola glass walls, pergola LED lighting, pergola financing Texas, Acorn Finance pergola, outdoor living contractor Seguin TX, Hill Country outdoor living, Boerne pergola, New Braunfels pergola, Seguin pergola",
    }),
    scripts: [
      ...pergolaHeadScripts({
        path: PATH,
        serviceName: "Motorized Louvered Pergolas",
        breadcrumbName: "Louvered Pergolas",
        visibleFaqs: PAGE_FAQS,
        description: DESC,
        faqs: [
          {
            q: "What is a motorized louvered pergola?",
            a: "It's a permanent aluminum outdoor structure with rotating roof slats. Open the louvers for breeze and sunlight, close them for a rain-resistant roof — water is caught in integrated gutters in the perimeter beams and drains down through the posts. Unlike a retractable awning, nothing folds away; you're buying an outdoor room.",
          },
          {
            q: "What configurations are available?",
            a: "Attached (mounted to the building on one side, posts on the other), freestanding (four or more posts, no building attachment), and multi-bay systems where several modules are coupled together for large patios, restaurants and commercial spaces.",
          },
          {
            q: "What does a louvered pergola cost in Texas?",
            a: "Planning ranges: a 12' x 16' system typically lands around $35,000–$48,000 installed and a 16' x 20' around $48,000–$65,000+. Add screens, lighting, fans, heaters or misting and complete outdoor-living projects commonly run $60,000–$120,000+. Site work, foundations, electrical, engineering and permits move the number, so every quote is written and itemized.",
          },
          {
            q: "Is financing available?",
            a: "Yes. Every louvered pergola and outdoor-living package qualifies for financing through Acorn Finance — checking your options takes about a minute and does not affect your credit score. Many customers finance the pergola and its upgrades together as one project.",
          },
          {
            q: "How do these hold up to Texas wind and rain?",
            a: "We install engineered systems only: sealed drawings where required, documented wind-load ratings, published foundation details, and rain/wind sensors that close or reposition the louvers automatically. Foundations, attachment engineering, drainage and permitting are part of the scope — not an afterthought.",
          },
          {
            q: "Can I add motorized screens and lighting later?",
            a: "Yes, but it is cheaper to plan for them now. We rough in wiring, screen pockets and mounting points during installation so screens, LED lighting, fans, heaters and misting can be added without opening the structure back up.",
          },
          {
            q: "Do you install louvered pergolas for restaurants and hotels?",
            a: "Yes. Multi-bay louvered systems turn patios into all-weather revenue space for restaurants, breweries, wineries, hotels, resorts, apartment pools, country clubs and event venues. We handle design drawings, engineering coordination and permitting.",
          },
          {
            q: "Which brand of louvered pergola do you install?",
            a: "Texas Made Shade sells PERGOLUX aluminum pergolas, including the manual Pergola 4, motorized Pergola 4 Pro and premium motorized Pergola 4 Pro Max, along with compatible screens, glass walls and lighting accessories.",
          },
        ],
      }),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Motorized Louvered Pergola Design & Installation",
          serviceType: "Louvered Pergola / Opening Roof System Installation",
          url: canonicalUrl(PATH),
          provider: { "@id": "https://texasmadeshade.com/#business" },
          brand: { "@type": "Brand", name: "PERGOLUX" },
          areaServed: [
            { "@type": "City", name: "San Antonio" },
            { "@type": "City", name: "Austin" },
            { "@type": "City", name: "New Braunfels" },
            { "@type": "City", name: "Seguin" },
            { "@type": "City", name: "Boerne" },
          ],
          offers: {
            "@type": "AggregateOffer",
            priceCurrency: "USD",
            lowPrice: 35000,
            highPrice: 120000,
            offerCount: 4,
            availability: "https://schema.org/InStock",
            availableDeliveryMethod: "http://purl.org/goodrelations/v1#DeliveryModeOwnFleet",
          },
        }),
      },
    ],
  }),
  component: LouveredPergolasPage,
});

function LouveredPergolasPage() {
  return (
    <ServicePage
      currentPath={PATH}
      serviceName="Louvered Pergolas"
      breadcrumbs={[
        { label: "Outdoor Living", to: "/hill-country-series" },
        { label: "Motorized Louvered Pergolas" },
      ]}
      eyebrow="★ HILL COUNTRY SERIES™ · OUTDOOR LIVING ★"
      title="PERGOLUX Pergolas & Outdoor Canopies"
      intro="Texas Made Shade now sells PERGOLUX aluminum pergolas and pergola accessories for outdoor living. Choose a manual Pergola 4 or step up to the motorized Pergola 4 Pro and Pergola 4 Pro Max with integrated lighting. Complete the structure with compatible retractable screens, glass walls and LED lighting. Available for customers across San Antonio, Austin, Seguin, New Braunfels, Boerne and the Hill Country — financing available through Acorn Finance."
      heroImage={hero}
      heroImageClassName="object-cover opacity-80"
      heroOverlayClassName="bg-gradient-to-r from-navy-deep via-navy-deep/75 to-navy-deep/10"
      heroContentClassName="relative mx-auto max-w-7xl px-4 py-28 md:py-40 lg:px-8"
      heroCta={{
        eyebrow: "PERGOLUX · HILL COUNTRY SERIES™",
        headline: "Choose My PERGOLUX Pergola",
        subhead:
          "Send patio dimensions and photos. We'll come out, measure, and put together a written, itemized outdoor-living proposal — structure, screens, lighting, electrical and site work broken out line by line. Financing available.",
        primaryHref: `/#lets-talk?type=${encodeURIComponent("Patio / Pergola Cover")}&notes=${encodeURIComponent("Motorized louvered pergola — outdoor living project.")}`,
        primaryLabel: "Start My Pergola Design",
        phoneNumber: "+1 (830) 390-9588",
      }}
      keywordOutline={[
        { id: "how-it-works", label: "How a louvered roof works" },
        { id: "pergolux-models", label: "PERGOLUX pergola models" },
        { id: "ladder", label: "Awning vs. pergola vs. structure" },
        { id: "configurations", label: "Attached, freestanding & multi-bay" },
        { id: "upgrades", label: "Upgrades & outdoor-living options" },
        { id: "commercial", label: "Commercial & hospitality systems" },
        { id: "engineering", label: "Engineering, foundations & permits" },
        { id: "financing", label: "Investment & financing" },
      ]}
      keywordOutlineHeading="On This Page"
      features={[
        "Powder-coated extruded aluminum posts, beams & louvers",
        "Motorized louvers — open for breeze, closed for rain",
        "Integrated gutters draining through the posts",
        "Optional rain & wind sensors with auto-close",
        "Attached, freestanding & multi-bay configurations",
        "Screens, lighting, fans, heaters, misting & automation",
        "Engineered for Texas wind loads with sealed drawings where required",
        "Financing available through Acorn Finance",
        "PERGOLUX manual and motorized Series 4 models",
      ]}
      body={[
        {
          heading: "How a Motorized Louvered Roof Works",
          text: "Aluminum louver blades ride on a motorized shaft inside the perimeter frame. Flat open gives you sky and breeze; tilted gives you filtered, moving shade that tracks the sun; fully closed laps the louvers like roof shingles so rain runs into the gutter channels built into the beams and drains down inside the posts. One remote, wall switch or phone app position change turns a sun deck into a covered room in about fifteen seconds.",
        },
        {
          heading: "Why It's a Different Purchase Than an Awning",
          text: "A retractable awning is fabric shade on demand at a lower investment. A louvered pergola is architecture: footings, posts, engineered spans, electrical and drainage. Customers who start by asking for shade over a patio or outdoor kitchen often discover that what they actually want is a permanent outdoor room they can furnish, light and use in the rain.",
          cta: { label: "Compare with retractable awnings", href: "/retractable-awnings" },
        },
        {
          heading: "PERGOLUX Pergolas, Sold by a Local Shade Specialist",
          text: "We help you compare the PERGOLUX Pergola 4, Pergola 4 Pro and Pergola 4 Pro Max, then plan the compatible screens, glass walls and lighting around the way you use your patio. You get local product guidance from a nearly 30-year shade and fabrication company instead of guessing through a large online catalog.",
          cta: { label: "See our Seguin fabrication shop", href: "/custom-fabrication" },
        },
      ]}
      applications={[
        "Backyard Outdoor Rooms",
        "Outdoor Kitchens & Bars",
        "Pool Decks & Spa Areas",
        "Fire Pit & Lounge Areas",
        "Restaurant & Brewery Patios",
        "Hotel, Resort & Glamping Decks",
        "Apartment & HOA Pool Areas",
        "Country Clubs & Event Venues",
      ]}
      applicationsEyebrow="APPLICATIONS"
      applicationsHeading="Where Louvered Pergolas Go"
      letsTalkImageSrc={hero}
      letsTalkImageAlt="Motorized aluminum louvered pergola with integrated lighting over a Texas patio at sunset"
      faqs={PAGE_FAQS}
      hideFaqJsonLd
      cityCallouts={[
        {
          to: "/louvered-patio-covers/in/san-antonio",
          city: "San Antonio",
          anchorText: "Motorized louvered patio covers in San Antonio, TX",
          blurb: "Louvered roof systems for Bexar County patios, pool decks and restaurant seating.",
        },
        {
          to: "/louvered-patio-covers/in/austin",
          city: "Austin",
          anchorText: "Motorized louvered patio covers in Austin, TX",
          blurb: "Opening-roof outdoor rooms across Austin, Round Rock and the eastern Hill Country.",
        },
        {
          to: "/hill-country-series",
          city: "Hill Country Series™",
          anchorText: "Hill Country Series™ Outdoor Living & Premium Shade",
          blurb: "The full luxury outdoor-living lineup: pergolas, screens, lighting, cooling and automation.",
        },
      ]}
      preContent={<LouveredSections />}
      postCta={<PergolaCityLinks hideHubLink />}
    />
  );
}

function LouveredSections() {
  return (
    <>
      <section id="pergolux-models" className="scroll-mt-24 border-t-4 border-navy-deep bg-white py-16">
        <div className="mx-auto max-w-6xl px-4 lg:px-8">
          <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ NOW SELLING PERGOLUX</div>
          <h2 className="mt-2 font-display text-3xl font-bold text-navy-deep md:text-4xl">
            PERGOLUX Pergolas &amp; Compatible Accessories
          </h2>
          <p className="mt-4 max-w-3xl text-navy-deep/80">
            Our PERGOLUX offering is focused on the pergola and accessory side of the catalog: aluminum louvered
            pergolas, retractable screens, glass walls and pergola lighting. We do not sell the unrelated furniture or
            general outdoor-product categories.
          </p>
          <div className="mt-8 grid gap-6 lg:grid-cols-3">
            {PERGOLUX_MODELS.map((model) => (
              <article key={model.name} className="overflow-hidden border-2 border-navy-deep/15 bg-cream">
                <img src={model.image} alt={model.alt} width={1400} height={1050} loading="lazy" className="aspect-[4/3] w-full object-cover" />
                <div className="p-6">
                  <div className="font-display text-xs font-bold uppercase tracking-wider text-red">{model.type}</div>
                  <h3 className="mt-2 font-display text-xl font-bold text-navy-deep">{model.name}</h3>
                  <p className="mt-3 text-sm text-navy-deep/80">{model.description}</p>
                  <ul className="mt-4 space-y-2 text-sm text-navy-deep/75">
                    {model.features.map((feature) => <li key={feature}>★ {feature}</li>)}
                  </ul>
                  <Link to="/estimate-quotes" className="mt-5 inline-flex bg-navy-deep px-4 py-3 font-display text-xs font-bold uppercase tracking-wider text-white transition hover:bg-red">
                    Request a Quote →
                  </Link>
                </div>
              </article>
            ))}
          </div>
          <div className="mt-8 border-l-4 border-red bg-cream p-6">
            <h3 className="font-display text-xl font-bold text-navy-deep">Pergola Accessories We Sell</h3>
            <p className="mt-2 text-navy-deep/80">
              Add PERGOLUX retractable screens for adjustable sun, privacy, wind and insect protection; tempered glass
              walls for a brighter weather-protected enclosure; or dimmable PERGOLUX LED lighting designed for the
              pergola groove system. Accessory compatibility depends on the pergola model and size. Request accessory
              pricing with your free quote.
            </p>
            <Link to="/estimate-quotes" className="mt-4 inline-flex font-display text-sm font-bold uppercase tracking-wider text-red underline">
              Get accessory pricing →
            </Link>
          </div>
        </div>
      </section>

      {/* Investment ladder */}
      <section id="ladder" className="scroll-mt-24 border-t-4 border-navy-deep bg-cream py-16">
        <div className="mx-auto max-w-6xl px-4 lg:px-8">
          <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ CHOOSING YOUR SYSTEM</div>
          <h2 className="mt-2 font-display text-3xl font-bold text-navy-deep md:text-4xl">
            Awning, Louvered Pergola, or Custom Structure?
          </h2>
          <p className="mt-4 max-w-3xl text-navy-deep/80">
            These don't compete — they're three rungs on the same ladder. Pick the one that matches how permanent you
            want the space to be and what you want to invest.
          </p>
          <ol className="mt-8 grid list-none gap-6 p-0 md:grid-cols-3">
            {LADDER.map((rung) => (
              <li
                key={rung.name}
                className={`flex flex-col border-4 p-6 ${
                  rung.featured
                    ? "border-red bg-white shadow-bold"
                    : "border-navy-deep/15 bg-white/70"
                }`}
              >
                <div className="font-display text-xs uppercase tracking-[0.2em] text-red">{rung.step}</div>
                <h3 className="mt-2 font-display text-xl font-bold text-navy-deep">{rung.name}</h3>
                <div className="mt-1 text-xs font-bold uppercase tracking-wider text-navy-deep/60">{rung.invest}</div>
                <p className="mt-3 flex-1 text-sm text-navy-deep/80">{rung.blurb}</p>
                {rung.featured ? (
                  <span className="mt-4 inline-block bg-navy-deep px-3 py-2 font-display text-xs uppercase tracking-wider text-white">
                    You're here
                  </span>
                ) : (
                  <Link
                    to={rung.to}
                    className="mt-4 inline-flex items-center gap-1 font-display text-xs font-bold uppercase tracking-wider text-red underline"
                  >
                    {rung.cta} →
                  </Link>
                )}
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Configurations */}
      <section id="configurations" className="scroll-mt-24 bg-white py-16">
        <div className="mx-auto max-w-6xl px-4 lg:px-8">
          <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ CONFIGURATIONS</div>
          <h2 className="mt-2 font-display text-3xl font-bold text-navy-deep md:text-4xl">
            Attached, Freestanding & Multi-Bay
          </h2>
          <div className="mt-8 grid gap-6 md:grid-cols-3">
            {CONFIGS.map((c) => (
              <div key={c.name} className="border-l-4 border-red bg-cream p-6">
                <h3 className="font-display text-xl font-bold text-navy-deep">{c.name}</h3>
                <p className="mt-2 text-sm text-navy-deep/80">{c.blurb}</p>
                <ul className="mt-4 space-y-1 text-xs font-bold uppercase tracking-wider text-navy-deep/70">
                  {c.specs.map((s) => (
                    <li key={s}>★ {s}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <p id="how-it-works" className="mt-8 scroll-mt-24 max-w-3xl text-sm text-navy-deep/75">
            Single zones typically span up to roughly 22' x 16' before the system needs a post or a second bay — final
            spans depend on the engineered wind and snow loading for your site, which we confirm before quoting.
          </p>
        </div>
      </section>

      {/* Upgrades */}
      <section id="upgrades" className="scroll-mt-24 border-y-4 border-navy-deep bg-navy-deep py-16 text-white">
        <div className="mx-auto max-w-6xl px-4 lg:px-8">
          <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ BUILD THE WHOLE ROOM</div>
          <h2 className="mt-2 font-display text-3xl font-bold md:text-4xl">Upgrades That Go On the Same Project</h2>
          <p className="mt-4 max-w-3xl text-white/80">
            The pergola is the shell. Everything that makes it a room — screens, light, air, heat, privacy and control —
            we build, wire and service ourselves.
          </p>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {UPGRADES.map((u) => (
              <Link
                key={u.name}
                to={u.to}
                className="block border-2 border-white/25 bg-white/5 p-5 transition hover:border-red hover:bg-white/10"
              >
                <h3 className="font-display text-base font-bold uppercase tracking-wider text-white">{u.name}</h3>
                <p className="mt-2 text-sm text-white/75">{u.blurb}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Commercial */}
      <section id="commercial" className="scroll-mt-24 bg-cream py-16">
        <div className="mx-auto max-w-6xl px-4 lg:px-8 md:grid md:grid-cols-2 md:gap-10">
          <div>
            <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ COMMERCIAL & HOSPITALITY</div>
            <h2 className="mt-2 font-display text-3xl font-bold text-navy-deep md:text-4xl">
              Turn Patio Square Footage Into Usable Seats
            </h2>
            <p className="mt-4 text-navy-deep/80">
              For a restaurant, brewery, winery, hotel, resort, apartment pool, country club or event venue, a
              multi-bay louvered system isn't decoration — it's revenue-producing square footage that stays open when
              the weather turns. Thirty to forty additional all-weather seats change the math on the investment
              entirely.
            </p>
            <p className="mt-4 text-navy-deep/80">
              We handle design drawings, engineered documentation, permitting coordination, electrical scheduling and
              phased installation so you don't lose your patio for a season.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/longhorn-series"
                className="border-4 border-navy-deep bg-white px-4 py-3 font-display text-xs font-bold uppercase tracking-wider text-navy-deep transition hover:bg-navy-deep hover:text-white"
              >
                Longhorn Series™ Commercial
              </Link>
              <Link
                to="/rio-grande-series"
                className="border-4 border-navy-deep bg-white px-4 py-3 font-display text-xs font-bold uppercase tracking-wider text-navy-deep transition hover:bg-navy-deep hover:text-white"
              >
                Rio Grande Series™ Hospitality
              </Link>
            </div>
          </div>
          <div id="engineering" className="mt-10 scroll-mt-24 border-4 border-navy-deep bg-white p-6 md:mt-0">
            <h3 className="font-display text-2xl font-bold text-navy-deep">
              Engineering, Foundations & Permitting
            </h3>
            <p className="mt-3 text-sm text-navy-deep/80">
              A louvered pergola is not a bolt-together kit. Texas wind requirements, footing design, attachment
              engineering, drainage, electrical and permitting all matter — and they're the reason we only install
              systems that come with real documentation.
            </p>
            <ul className="mt-4 space-y-2 text-sm text-navy-deep/80">
              {[
                "Engineered drawings & sealed documents where jurisdictions require them",
                "Published wind-load ratings for the configuration you're buying",
                "Manufacturer foundation & footing requirements followed exactly",
                "Drainage planned away from foundations, walks & outdoor kitchens",
                "Licensed electrical coordination for motors, lighting & sensors",
                "Permit & HOA submittal packages prepared for you",
                "Factory dealer training, parts availability & transferable warranty",
              ].map((l) => (
                <li key={l} className="border-l-4 border-red pl-3">
                  {l}
                </li>
              ))}
            </ul>
            <p className="mt-4 text-xs uppercase tracking-wider text-navy-deep/60">
              Texas Made Shade now sells PERGOLUX pergolas and compatible pergola accessories.
            </p>
          </div>
        </div>
      </section>

      {/* Financing */}
      <section id="financing" className="scroll-mt-24 border-t-4 border-navy-deep bg-white py-16">
        <div className="mx-auto max-w-5xl px-4 text-center lg:px-8">
          <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ FINANCING AVAILABLE</div>
          <h2 className="mt-2 font-display text-3xl font-bold text-navy-deep md:text-4xl">
            Every Louvered Pergola Qualifies for Acorn Finance
          </h2>
          <p className="mx-auto mt-4 max-w-3xl text-navy-deep/80">
            Louvered pergolas, motorized screens, Bosso smart lighting, fans, heaters, misting and automation can all be
            financed together as one outdoor-living project through Acorn Finance. Checking your options takes about a
            minute and does not affect your credit score — use the <strong>Financing Available</strong> button in the
            corner of any page to see monthly payment options before you decide on size and upgrades.
          </p>
          <div className="mx-auto mt-8 grid max-w-3xl gap-4 sm:grid-cols-3 text-left">
            {[
              { h: "12' × 16'", p: "$35,000–$48,000 installed" },
              { h: "16' × 20'", p: "$48,000–$65,000+ installed" },
              { h: "Full Outdoor Room", p: "$60,000–$120,000+ with screens, light, air & heat" },
            ].map((t) => (
              <div key={t.h} className="border-4 border-navy-deep/15 bg-cream p-5">
                <div className="font-display text-lg font-bold text-navy-deep">{t.h}</div>
                <div className="mt-1 text-sm text-navy-deep/75">{t.p}</div>
              </div>
            ))}
          </div>
          <p className="mx-auto mt-6 max-w-3xl text-xs text-navy-deep/60">
            Planning ranges only — not a quote. Site work, foundations, electrical, engineering, permits, spans and
            options change the final price. Every Texas Made Shade proposal is written and itemized.
          </p>
        </div>
      </section>
    </>
  );
}
